import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';
import CustomFieldInput from '../authentication/configuracoes/shared/CustomFieldInput';
import { isFieldVisible } from '../authentication/configuracoes/shared/fieldVisibility';
import EntityPickerInline from './EntityPickerInline';
import { PRIORITY_LABELS, STATUS_LABELS } from './ticketUtils';

// Formulário de criação de Ticket - reaproveita EntityPickerInline.js pros
// campos de busca (Responsável/Cliente/Organização) e CustomFieldInput.js
// (o mesmo componente já usado por Organização/Cliente/Negócio/Produto/
// Evento) pros campos customizados de Ticket. Aceita ids de
// pré-preenchimento vindos de RelatedTicketsSection (criar a partir de
// uma Conversation/Deal) - continuam editáveis.
const TicketFormDrawer = ({
    appId,
    initialConversationId,
    initialDealId,
    initialContactId,
    initialOrganizationId,
    onClose,
    onSaved
}) => {
    const [form, setForm] = useState({
        title: '',
        description: '',
        status: 'new',
        priority: 'normal',
        team: '',
        first_response_due_at: '',
        resolution_due_at: ''
    });
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [teams, setTeams] = useState([]);

    const [contactSelected, setContactSelected] = useState(null);
    const [orgSelected, setOrgSelected] = useState(null);
    const [assigneeSelected, setAssigneeSelected] = useState(null);

    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');

    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [fields, setFields] = useState({});

    useEffect(() => {
        if (!appId) return;
        api
            .get(`v1/apps/${appId}/teams/`)
            .then((response) => setTeams(response.data?.teams || []))
            .catch((error) => console.error('Erro ao carregar equipes', error));
        api
            .get(`v1/apps/${appId}/ticket_fields/`)
            .then((response) => setFieldsConfig(response.data || []))
            .catch((error) => console.error('Erro ao carregar campos customizados', error));
    }, [appId]);

    // Se veio com contactId/organizationId pré-preenchido (criado a partir
    // de uma Conversation/Deal), busca os dados só pra mostrar o nome.
    useEffect(() => {
        if (!appId || !initialContactId) return;
        api
            .get(`v1/apps/${appId}/users/${initialContactId}/`)
            .then((response) => setContactSelected(response.data))
            .catch(() => {});
    }, [appId, initialContactId]);

    useEffect(() => {
        if (!appId || !initialOrganizationId) return;
        api
            .get(`v1/apps/${appId}/organizations/${initialOrganizationId}/`)
            .then((response) => setOrgSelected(response.data?.organization))
            .catch(() => {});
    }, [appId, initialOrganizationId]);

    const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const addTag = () => {
        const value = tagInput.trim();
        if (!value || tags.includes(value)) return;
        setTags([...tags, value]);
        setTagInput('');
    };

    const removeTag = (value) => setTags(tags.filter((t) => t !== value));

    const handleSubmit = async () => {
        if (!form.title.trim()) {
            setStatus({ type: 'error', message: 'Informe o título do Ticket.' });
            return;
        }
        setSaving(true);
        setStatus({ type: '', message: '' });
        try {
            const payload = {
                title: form.title,
                description: form.description,
                status: form.status,
                priority: form.priority,
                team: form.team || null,
                assigned_to: assigneeSelected?.id || null,
                contact: contactSelected?.id || null,
                organization: orgSelected?.id || null,
                conversation: initialConversationId || null,
                deal: initialDealId || null,
                tags,
                first_response_due_at: form.first_response_due_at || null,
                resolution_due_at: form.resolution_due_at || null,
                fields
            };
            await api.post(`v1/apps/${appId}/tickets/`, payload);
            if (onSaved) onSaved();
        } catch (error) {
            console.error('Erro ao criar ticket', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar Ticket.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" fontWeight={700}>
                        Novo Ticket
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {status.message && (
                    <Alert severity={status.type} sx={{ mb: 2 }}>
                        {status.message}
                    </Alert>
                )}

                {(initialConversationId || initialDealId) && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {initialConversationId && <Chip size="small" label="Conversa vinculada" />}
                        {initialDealId && <Chip size="small" label="Negócio vinculado" />}
                    </Box>
                )}

                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField label="Título" value={form.title} onChange={handleChange('title')} fullWidth required autoFocus />
                    <TextField
                        label="Descrição"
                        value={form.description}
                        onChange={handleChange('description')}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    <Box display="flex" gap={2}>
                        <TextField select label="Status" value={form.status} onChange={handleChange('status')} fullWidth>
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField select label="Prioridade" value={form.priority} onChange={handleChange('priority')} fullWidth>
                            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <TextField select label="Equipe" value={form.team} onChange={handleChange('team')} fullWidth>
                        <MenuItem value="">Nenhuma</MenuItem>
                        {teams.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <EntityPickerInline
                        label="Responsável"
                        placeholder="Buscar usuário por nome ou e-mail..."
                        searchUrl={(term) => `users/?search=${term}`}
                        extractResults={(data) => data}
                        getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`}
                        value={assigneeSelected}
                        onSelect={setAssigneeSelected}
                        onClear={() => setAssigneeSelected(null)}
                        clearLabel="Sem responsável"
                    />

                    <EntityPickerInline
                        label="Cliente"
                        placeholder="Buscar cliente por nome ou e-mail..."
                        searchUrl={(term) => `v1/apps/${appId}/users/?search=${term}`}
                        extractResults={(data) => data.users}
                        getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`}
                        value={contactSelected}
                        onSelect={setContactSelected}
                        onClear={() => setContactSelected(null)}
                        clearLabel="Sem cliente"
                    />

                    <EntityPickerInline
                        label="Organização"
                        placeholder="Buscar organização..."
                        searchUrl={(term) => `v1/apps/${appId}/organizations/?search=${term}`}
                        extractResults={(data) => data.organizations}
                        getOptionLabel={(org) => org.name}
                        value={orgSelected}
                        onSelect={setOrgSelected}
                        onClear={() => setOrgSelected(null)}
                        clearLabel="Sem organização"
                    />

                    {/* Prazos (SLA) - só os campos, sem cálculo automático nenhum */}
                    <Box display="flex" gap={2}>
                        <TextField
                            label="Prazo 1ª resposta"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={form.first_response_due_at}
                            onChange={handleChange('first_response_due_at')}
                        />
                        <TextField
                            label="Prazo de resolução"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={form.resolution_due_at}
                            onChange={handleChange('resolution_due_at')}
                        />
                    </Box>

                    {/* Tags */}
                    <Box>
                        <TextField
                            label="Tags"
                            fullWidth
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addTag();
                                }
                            }}
                            placeholder="Digite e pressione Enter"
                        />
                        {tags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {tags.map((tag) => (
                                    <Chip key={tag} label={tag} size="small" onDelete={() => removeTag(tag)} />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Campos customizados - mesmo componente já usado pra
                        Organização/Cliente/Negócio/Produto/Evento */}
                    {fieldsConfig.length > 0 && (
                        <Grid container spacing={2}>
                            {fieldsConfig
                                .filter((f) => isFieldVisible(f, fieldsConfig, fields))
                                .map((f) => (
                                    <CustomFieldInput
                                        key={f.id}
                                        field={f}
                                        value={fields[f.name]}
                                        onChange={(value) => setFields({ ...fields, [f.name]: value })}
                                    />
                                ))}
                        </Grid>
                    )}

                    <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                        <Button variant="outlined" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                            {saving ? <CircularProgress size={20} /> : 'Criar Ticket'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default TicketFormDrawer;
